import type { ChatEchange, ChatReponse } from '../composants/CentralisChat/chat.types';

/** Longueur maximale d'une question, alignee sur la validation du serveur. */
export const CHAT_LONGUEUR_MAX = 2000;

/** Nombre d'echanges renvoyes au serveur. Le serveur retronque de son cote. */
export const CHAT_ECHANGES_TRANSMIS = 10;

/**
 * Erreur portant un message deja formule pour l'utilisateur.
 *
 * Le serveur renvoie des messages actionnables (quota atteint, assistant
 * indisponible) : les afficher tels quels vaut mieux que de les remplacer par un
 * « une erreur est survenue » qui ne dit rien.
 */
export class ChatError extends Error {
  constructor(message: string, public readonly statut: number) {
    super(message);
    this.name = 'ChatError';
  }
}

/**
 * Identifiant du fil de conversation, genere une fois par affichage du widget.
 *
 * Le serveur ne le fabrique pas et ne le renvoie pas : il le recopie sur chaque
 * question journalisee, ce qui permet au back-office de relire une conversation
 * entiere au lieu de questions isolees. UUID v4 : `randomUUID` quand le navigateur
 * l'expose (contexte securise), sinon le meme format a partir de `getRandomValues`,
 * disponible partout.
 */
export const nouvelIdentifiantConversation = (): string => {
  const generateur = crypto as Crypto & { randomUUID?: () => string };

  if (typeof generateur.randomUUID === 'function') {
    return generateur.randomUUID();
  }

  const octets = crypto.getRandomValues(new Uint8Array(16));
  octets[6] = (octets[6] & 0x0f) | 0x40;
  octets[8] = (octets[8] & 0x3f) | 0x80;
  const hex = Array.from(octets, (octet) => octet.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Pose une question au chatbot d'aide.
 *
 * Le fil est transmis a chaque appel : le serveur ne le conserve pas et ne le
 * renvoie pas, il vit dans cet onglet et disparait a sa fermeture. L'identifiant de
 * conversation, lui, est recopie sur chaque question journalisee cote serveur.
 */
export const envoyerMessage = async (
  message: string,
  historique: ChatEchange[],
  conversationId: string,
  signal?: AbortSignal,
): Promise<ChatReponse> => {
  const token = localStorage.getItem('token');

  if (!token) {
    throw new ChatError("Votre session a expiré. Reconnectez-vous pour utiliser l'assistant.", 401);
  }

  const response = await fetch(`${process.env.REACT_APP_API_URL}/chatbot/message`, {
    method: 'POST',
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      message: message.slice(0, CHAT_LONGUEUR_MAX),
      historique: historique.slice(-CHAT_ECHANGES_TRANSMIS),
      conversation_id: conversationId,
    }),
  });

  const corps = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ChatError(
      corps?.message ?? "L'assistant est momentanément indisponible. Réessayez dans un instant.",
      response.status,
    );
  }

  return corps.data as ChatReponse;
};

/**
 * Le chatbot est-il ouvert aux utilisateurs ?
 *
 * L'interrupteur vit en back-office : un administrateur peut couper l'assistant sans
 * deploiement. Le widget interroge cet etat au montage et disparait quand il est
 * ferme. Le serveur refuse de son cote — ce n'est pas au front de garantir la coupure.
 *
 * En cas d'echec (reseau, session expiree), on repond VRAI : un appel d'etat rate ne
 * doit pas faire disparaitre une aide qui fonctionne. Si le chatbot est reellement
 * coupe, l'envoi d'une question le dira avec son propre message.
 */
export const chatbotEstActif = async (signal?: AbortSignal): Promise<boolean> => {
  const token = localStorage.getItem('token');

  if (!token) {
    return false;
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/chatbot/etat`, {
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      return true;
    }

    const corps = await response.json();

    return corps?.data?.actif !== false;
  } catch {
    return true;
  }
};
